// @ts-nocheck
import Auth from "@/services/Auth";
import storage from "@/storage/storage";
import { Api } from "@/services/Api";
import qs from "query-string";
import { FilterOperator } from "@/models/FilterOperator";
import store from "@/store";
import PermissionHelper from "@/permissions/PermissionHelper";
import config from "@/services/Api-config";
const showToast = ({ state, commit }, message: string) => {
  if (state.toast.show) commit("hideToast");

  setTimeout(() => {
    commit("showToast", {
      color: "black",
      message,
      timeout: 3000
    });
  });
};

const showError = ({ state, commit }, { message = "Failed!", error }) => {
  if (state.toast.show) commit("hideToast");

  setTimeout(() => {
    commit("showToast", {
      color: "error",
      message: message + " " + error.message,
      timeout: 10000
    });
  });
};

const showSuccess = ({ state, commit }, message: string) => {
  if (state.toast.show) commit("hideToast");
  setTimeout(() => {
    commit("showToast", {
      color: "success",
      message,
      timeout: 3000
    });
  });
};

async function getMyHotels({ commit, state }){
  const myHotel = await storage.getItem("my-hotels");
  if (typeof myHotel == "undefined" || myHotel == null){
    commit("setPageLoading", true);
    try {
      const token = await Auth.GetTokenPayload();
      const res = await Api.get(`/my/hotels/${ token.uid }/v1`);
      if (res && res.data && res.data.Data) {
        state.hotels = res.data.Data;
        storage.setItem("my-hotels", res.data);
      } else {
        state.hotels = [];
      }
    } catch (err) {
      console.error("Failed to fetch hotels:", err);
      state.hotels = [];
    } finally {
      commit("setPageLoading", false);
    }
  } else {
    state.hotels = myHotel.Data;
  }
}

async function getHotelChains({ state }){
  const hotelChains = await storage.getItem("hotel-chains");
  if (typeof hotelChains == "undefined" || hotelChains == null) {
    try {
      const token = await Auth.GetTokenPayload();
      const res = await Api.get(`/my/hotel-chains/${ token.uid }/v1`);
      if (res && res.data && res.data.Data) {
        state.hotelChains = res.data.Data;
        storage.setItem("hotel-chains", res.data);
      } else {
        state.hotelChains = [];
      }
    } catch (err) {
      console.error("Failed to fetch hotel chains:", err);
      state.hotelChains = [];
    }
  } else {
    state.hotelChains = hotelChains.Data || [];
  }
}

async function getMyProfile({ state }){
  const myProfile = await storage.getItem("user");
  if (typeof myProfile == "undefined" || myProfile == null){
    try {
      const token = await Auth.GetTokenPayload();
      const res = await Api.get(`/my/profile/${ token.uid }/v1`);
      if (res && res.data && res.data.Data) {
        state.user = res.data.Data;
        storage.setItem("user", res.data);
        PermissionHelper.storeAndEncryptUserInformation(res.data);

        const googleCredential = typeof res.data.Data.google_credential != "undefined" && res.data.Data.google_credential ?
          res.data.Data.google_credential : "";
        if (googleCredential.toString().length > 1){
          localStorage.setItem("google-credential", googleCredential);
        }
        if ((<IMyProfile>res.data.Data).force_change_password){
          PermissionHelper.removeEncryptedUserInformation();
          window.location.href = `${ config.ST_WEB_Account }/my-account/change-password?force=1&next=${ window.location.href }`;
        }
      } else {
        state.user = {};
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      state.user = {};
    }
  } else {
    state.user = myProfile.Data;
  }
}

/**
 * How long a cached `my-outlets` payload is served before it is refreshed.
 *
 * Short enough that a setting published from another device shows up within a
 * shift change, long enough that opening the table list is still one cached read
 * for a waiter who bounces between tables all evening.
 */
const MY_OUTLETS_MAX_AGE_MS = 5 * 60 * 1000;

/**
 * In-flight `/my/outlets` requests, keyed by hotel.
 *
 * Api.get keeps ONE AbortController per base path, so two concurrent calls to this
 * endpoint abort each other: on first load ToolbarHotels (bootstrap) and TableList
 * (refresh) both dispatch, one request dies, and if the bootstrap call is the one
 * aborted it rethrows into an un-caught mounted(). Concurrent callers share a single
 * request instead.
 */
const myOutletsInFlight = new Map<number, Promise<unknown[]>>();

/** True when `state.outlets` already holds this hotel's list. */
function hasOutletsForHotel(state, hotelId: number): boolean {
  return state.outlets?.length > 0 && state.outlets[0].hotel_id === hotelId;
}

/** One shared request per hotel while it is in flight. */
function fetchMyOutlets(hotelId: number): Promise<unknown[]> {
  const pending = myOutletsInFlight.get(hotelId);
  if (pending) return pending;

  const request = (async () => {
    const token = await Auth.GetTokenPayload();
    const res = await Api.get(`/my/outlets/${token.uid}/v1?hotelId=${hotelId}`,null,{ maxAgeInSecond: 0 });
    return res?.data?.Data || [];
  })().finally(() => myOutletsInFlight.delete(hotelId));

  myOutletsInFlight.set(hotelId, request);
  return request;
}

async function getMyOutlets({ commit, state }, payload: {hotelId: number; force?: boolean}): Promise<void>{
  const myOutlet = await storage.getItem("my-outlets");
  const cached = myOutlet?.Data;
  // The cache is usable when it holds a non-empty list for the hotel being asked
  // about — the same test this action has always applied.
  const hasUsableCache = cached?.length > 0 && cached[0].hotel_id === payload.hotelId;
  /*
    PMS-763. `my-outlets` used to be written once and then removed only on logout
    (PermissionHelper.removePermission), so on a device that stays signed in — which
    is every POS tablet — the outlet row was frozen at first sign-in FOREVER.

    That hid the spatial table layout: the Layout toggle in Order Management is
    gated on the outlet's `layout_enabled` (TableList.hasPublishedLayout), read from
    this cache. A layout published in Master Data → POS → Tables rendered correctly
    in the designer, which fetches the outlet live, but Order Management kept
    serving a pre-feature cached row that has no `layout_enabled` key at all, so the
    toggle never appeared and the layout was unreachable. Every other outlet-level
    setting was equally frozen.

    So: serve the cache, but refresh it past a max age, and immediately when a
    caller knows the row just changed (`force`).
  */
  const isStale = Date.now() - (myOutlet?.fetched_at ?? 0) > MY_OUTLETS_MAX_AGE_MS;

  /*
    Assign only when state does not already hold this hotel's list.

    storage.getItem deserialises a FRESH array of fresh objects on every read, and
    Vue 2 watchers compare by identity — so re-assigning an identical list makes
    ToolbarOutlets' `selectedOutlet` watcher fire, which calls setSelectedOutlet()
    and getMyWorkingDay(), i.e. two more GETs. TableList is re-created on every
    return from an order (tableComponentKey), so an unconditional assignment turned
    a free cache hit into two requests per order.
  */
  const alreadyLoaded = hasOutletsForHotel(state, payload.hotelId);
  if (hasUsableCache && !alreadyLoaded) state.outlets = cached;

  // Serving the cache is the whole fast path — no request, no state churn.
  if (hasUsableCache && !payload?.force && !isStale) return;

  try {
    const data = await fetchMyOutlets(payload.hotelId);
    /*
      Only a non-empty response may replace what we hold. Api.get resolves offline
      with whatever the response cache has — possibly null — and persisting an empty
      list would strand the user with no outlets until the next logout.
    */
    if (data.length > 0) {
      state.outlets = data;
      await storage.setItem("my-outlets", { Data: data, fetched_at: Date.now() });
    }
  } catch (err) {
    // A background refresh must never break a page that already has outlets. The
    // bootstrap path (nothing usable cached) still reports the failure to its caller.
    if (!hasUsableCache) throw err;
    console.error("Failed to refresh outlets:", err);
  } finally {
    // Only the blocking bootstrap fetch owns the page spinner, as before. A refresh
    // that happens behind a rendered page must not clear a spinner it never raised.
    if (!hasUsableCache) commit("setPageLoading", false);
  }
}

// Get current working day
async function getMyWorkingDay({ commit, state }, data: Object) {
  if (!data.outletId || data.outletId === "undefined" || isNaN(data.outletId)) {
    commit("setPageLoading", false);
    return;
  }

  commit("setPageLoading", true);
  const params = qs.stringify(
    {
      filter_by: ["outlet_id"],
      filter_by_value: [data.outletId],
      filter_by_operator: [FilterOperator.Equal],
      sort_by: ["working_date"],
      sort_by_desc: [true],
      page: 1,
      page_size: 2
    },
    { arrayFormat: "bracket" }
  );
  const paramsPOSParameter = qs.stringify(
    {
      filter_by: ["outlet_id"],
      filter_by_value: [data.outletId],
      filter_by_operator: [FilterOperator.Equal],
      page: 1,
      page_size: 1
    },
    { arrayFormat: "bracket" }
  );

  Auth.GetTokenPayload()
    .then(token => {
    // Api.get(`/hotel/{hotelId}/dto/pos.closed-day/v1?${params}&userId=${token.uid}`)
      Promise.all([
        Api.get(`/hotel/{hotelId}/pos/{outletId}/dto/pos.closed-day/v1?${params}`,null, { maxAgeInSecond: 0 }),
        Api.get(`/hotel/{hotelId}/pos/{outletId}/dto/pos.parameter/v1?${paramsPOSParameter}`),
      ])
        .then(res => {
          const rData = res[0].data.Data?.filter(el => !el.closed_by);
          if (rData && rData.length > 0) {
            storage.setItem(`closed_day_${rData[0].working_date}`, rData[0]);
            state.closed_day = rData[0];
          } else {
            state.closed_day = {};
          }
          state.last_closed_day = res[0].data.Data?.[0] || {};
          if (res[1]?.data?.Data?.length) {
            state.parameter = res[1].data.Data[0];
          }
        }).finally(() => {
          commit("setPageLoading", false);
        });
    });
}

async function setWorkingDate({ commit }, workingDate) {
  commit("setWorkingDate", workingDate);
}

async function setPageLoading({ commit }, loading) {
  commit("setPageLoading", loading);
}

async function setSyncing({ commit }, isSyncing) {
  commit("setSyncing", isSyncing);
}

export default {
  showToast,
  showError,
  showSuccess,
  getMyHotels,
  getHotelChains,
  getMyProfile,
  getMyOutlets,
  setSyncing,
  setPageLoading,
  getMyWorkingDay,
  setWorkingDate
};
